import React, { useRef, useState, useEffect } from "react";
import { FaSyncAlt, FaCamera, FaTimes, FaCheck, FaRedo } from "react-icons/fa";
import BaseModalAnimated from "./BaseModalAnimated";

const CameraPhotoModal = ({ modal, setModal, validation, confirm }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [facingMode, setFacingMode] = useState("environment"); // Usually drivers need the back camera for documents
    const [stream, setStream] = useState(null);
    const [photo, setPhoto] = useState(false);

    useEffect(() => {
        if (modal.show) {
            startCamera();
        } else {
            stopCamera();
        }
        return () => stopCamera(); // Cleanup on unmount
    }, [modal.show, facingMode]);

    const stopCamera = () => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
    };

    const startCamera = async () => {
        stopCamera(); // Ensure old stream is killed
        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: { 
                    facingMode: facingMode,
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                },
            });
            setStream(mediaStream);
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
            }
        } catch (error) {
            console.error("Error accessing camera:", error);
            alert("Could not access camera. Please check permissions.");
        }
    };

    const takePhoto = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");

        // Match canvas size to video stream
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // If front camera, mirror the saved image
        if (facingMode === "user") {
            context.translate(canvas.width, 0);
            context.scale(-1, 1);
        }

        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        const imageData = canvas.toDataURL("image/jpeg", 0.8); // JPEG is better for uploads
        
        const fieldName = modal.inputName || 'imageProfile';
        validation.setFieldValue(fieldName, imageData);
        validation.setFieldValue(`${fieldName}_format`, 'image/jpeg');
        validation.setFieldValue(`${fieldName}_name`, `Photo_${Date.now()}.jpg`);
        validation.setFieldValue('camera', true);
        
        setPhoto(imageData);
        if (!confirm) {
            handleClose();
        }
        stopCamera();
    };

    const resetCamera = () => {
        setPhoto(false);
        startCamera();
    };

    const switchCamera = () => {
        setFacingMode(prev => (prev === "user" ? "environment" : "user"));
    };

    const handleClose = () => {
        setModal({ ...modal, show: false });
        stopCamera();
    };

    return (
        <BaseModalAnimated modal={modal} setModal={setModal}>
            <div className="flex flex-col items-center bg-cs-dark overflow-hidden rounded-3xl shadow-2xl w-full max-w-md mx-auto">
                
                {/* Header Area */}
                <div className="w-full p-1 flex justify-between items-center bg-white border-b border-cs-border">
                    <h3 className="font-bold text-cs-dark tracking-tight">
                        {photo ? "Confirm Evidence" : "Take Photo"}
                    </h3>
                    <button onClick={handleClose} className="text-cs-muted hover:text-cs-danger transition-colors">
                        <FaTimes size={20} />
                    </button>
                </div>

                {/* Viewfinder Area */}
                <div className="relative w-full aspect-[4/4] bg-black flex items-center justify-center">
                    {photo ? (
                        <img src={photo} className="w-full h-full object-contain" alt="Captured" />
                    ) : (
                        <>
                            <video 
                                ref={videoRef} 
                                autoPlay 
                                playsInline 
                                className={`w-full h-full object-cover ${facingMode === 'user' ? 'scale-x-[-1]' : ''}`}
                            />
                            {/* Overlay Guides */}
                            <div className="absolute inset-0 border-[20px] border-black/20 pointer-events-none flex items-center justify-center">
                                <div className="w-full h-full border border-white/30 rounded-lg"></div>
                            </div>
                            
                            {/* Switch Camera Button */}
                            <button 
                                onClick={switchCamera} 
                                className="absolute top-4 right-4 bg-white/20 backdrop-blur-md text-white p-3 rounded-full hover:bg-white/40 transition-all active:scale-90"
                            >
                                <FaSyncAlt size={18} />
                            </button>
                        </>
                    )}
                </div>

                {/* Footer Controls */}
                <div className="w-full p-1 bg-white flex justify-center items-center gap-4">
                    {photo ? (
                        <>
                            <button 
                                onClick={resetCamera} 
                                className="flex flex-col items-center gap-1 text-cs-muted active:scale-90 transition-transform"
                            >
                                <div className="w-12 h-12 rounded-full bg-cs-bg-alt flex items-center justify-center border border-cs-border">
                                    <FaRedo size={18} />
                                </div>
                                <span className="text-[10px] font-bold uppercase tracking-widest">Retake</span>
                            </button>

                            <button 
                                onClick={handleClose} 
                                className="flex flex-col items-center gap-1 text-cs-success active:scale-90 transition-transform"
                            >
                                <div className="w-16 h-16 rounded-full bg-cs-success text-white flex items-center justify-center shadow-lg shadow-cs-success/30">
                                    <FaCheck size={24} />
                                </div>
                                <span className="text-[10px] font-bold uppercase tracking-widest">Accept</span>
                            </button>
                        </>
                    ) : (
                        <button 
                            onClick={takePhoto} 
                            className="group flex flex-col items-center gap-2 active:scale-90 transition-transform"
                        >
                            <div className="w-20 h-20 rounded-full border-4 border-cs-bg-alt p-1">
                                <div className="w-full h-full rounded-full bg-cs-primary flex items-center justify-center text-white shadow-xl shadow-cs-primary/20">
                                    <FaCamera size={28} />
                                </div>
                            </div>
                            <span className="text-[10px] font-bold text-cs-muted uppercase tracking-widest">Capture</span>
                        </button>
                    )}
                </div>
            </div>
            {/* Hidden canvas for processing */}
            <canvas ref={canvasRef} className="hidden"></canvas>
        </BaseModalAnimated>
    );
};

export default CameraPhotoModal;